import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { Button, Input } from '@components/ui'
import { submitExitDemoLead } from '@services/exitDemoRequest'
import { markExitPopupSeen } from '@utils/landingSession'

type ExitIntentPopupProps = {
  open: boolean
  heroVariantIndex: number
  onClose: () => void
}

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void
    gtag?: (...args: unknown[]) => void
  }
}

function trackExitPopupConversion(heroVariantIndex: number): void {
  const variant = String(heroVariantIndex + 1)

  if (typeof window.fbq === 'function') {
    window.fbq('trackCustom', 'ExitPopupLead', { variant })
  }

  if (typeof window.gtag === 'function') {
    window.gtag('event', 'exit_popup_conversion', {
      event_category: 'exit_intent',
      variant,
    })
  }
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

export function ExitIntentPopup({ open, heroVariantIndex, onClose }: Readonly<ExitIntentPopupProps>) {
  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const handleClose = useCallback(() => {
    onClose()
  }, [onClose])

  useEffect(() => {
    if (!open) return

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose()
    }

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', onKeyDown)

    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open, handleClose])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    const trimmed = email.trim()

    if (!isValidEmail(trimmed)) {
      setEmailError('Introduza um email válido.')
      return
    }

    setEmailError(null)
    setSubmitError(null)
    setLoading(true)

    try {
      await submitExitDemoLead({ email: trimmed, heroVariant: heroVariantIndex })
      trackExitPopupConversion(heroVariantIndex)
      markExitPopupSeen()
      setSubmitted(true)
    } catch {
      setSubmitError('Não foi possível enviar. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  if (!open) return null

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 p-4"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose()
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="exit-popup-title"
        className="relative w-full max-w-md rounded-xl border border-secondary-700 bg-[#0a1628] p-8 text-center text-white shadow-2xl sm:p-10"
      >
        <button
          type="button"
          onClick={handleClose}
          className="absolute right-4 top-4 rounded-md p-1 text-white/50 transition-colors hover:text-white"
          aria-label="Fechar"
        >
          <X className="h-5 w-5" aria-hidden />
        </button>

        {submitted ? (
          <div className="px-2 py-4">
            <div className="mb-4 text-4xl text-primary-400" aria-hidden>
              ✓
            </div>
            <h2 id="exit-popup-title" className="mb-3 text-2xl font-bold">
              Enviado!
            </h2>
            <p className="text-sm leading-relaxed text-white/65">
              Enviámos o link da demonstração para o seu email. Verifique também a pasta de spam.
            </p>
            <Button type="button" variant="outline" className="mt-6" onClick={handleClose}>
              Fechar
            </Button>
          </div>
        ) : (
          <>
            <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-primary-400">
              Antes de sair
            </p>
            <h2 id="exit-popup-title" className="mb-3 text-2xl font-bold leading-snug">
              Veja o software a funcionar
              <br />
              numa oficina real, em poucos minutos
            </h2>
            <p className="mb-6 text-sm leading-relaxed text-white/60">
              Receba uma demonstração gravada no seu email.
              <br />
              Sem compromisso, sem cartão de crédito.
            </p>

            <form onSubmit={(e) => void handleSubmit(e)} className="text-left">
              <div className="flex flex-col gap-3 sm:flex-row">
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value)
                    if (emailError) setEmailError(null)
                  }}
                  placeholder="O seu email"
                  autoComplete="email"
                  className="border-secondary-700 bg-[#0f2040] text-white placeholder:text-white/40"
                  error={emailError ?? undefined}
                  disabled={loading}
                />
                <Button
                  type="submit"
                  className="shrink-0 whitespace-nowrap sm:px-5"
                  disabled={loading}
                  isLoading={loading}
                >
                  Enviar demo
                </Button>
              </div>
              {submitError ? <p className="mt-3 text-sm text-red-400">{submitError}</p> : null}
            </form>

            <p className="mt-4 text-xs text-white/45">Não partilhamos o seu email com ninguém.</p>
          </>
        )}
      </div>
    </div>,
    document.body
  )
}
